import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoModal from "@/components/feature/VideoModal";
import type { Streamer, NewsItem, VideoItem, GalleryItem } from "@/types/content";
import { streamers } from "@/mocks/streamers";
import { news } from "@/mocks/news";
import { videos } from "@/mocks/videos";
import { gallery } from "@/mocks/gallery";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import LatestNews from "@/pages/home/components/LatestNews";
import HotStreamers from "@/pages/home/components/HotStreamers";
import HotVideos from "@/pages/home/components/HotVideos";
import FeaturedGallery from "@/pages/home/components/FeaturedGallery";

const allStreamers = streamers as Streamer[];
const allNews = news as NewsItem[];
const allVideos = videos as VideoItem[];
const allGallery = gallery as GalleryItem[];

export default function Home() {
  const [playing, setPlaying] = useState<VideoItem | null>(null);

  const heroItems = allNews.slice(0, 4);
  const latestNews = [...allNews].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 10);
  const hotStreamers = [...allStreamers].sort((a, b) => b.heat - a.heat).slice(0, 6);
  const hotVideos = [...allVideos].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 4);
  const featuredGallery = allGallery.slice(0, 4);

  return (
    <>
      <Seo
        title="韩国女主播最新动态与精彩视频资讯"
        description="韩国女主播网站提供最新的韩国女主播资讯、视频和图片内容，涵盖多位知名主播的动态与专业资料，助您全面了解韩国女主播圈内信息。"
        keywords="韩国女主播,韩国主播资讯,韩国女主播视频,韩国娱乐新闻,韩国主播写真,最新韩国女主播,韩国女主播动态"
        canonical="/"
      />

      <div className="mx-auto max-w-[1440px] px-4 md:px-8 py-6 md:py-8 space-y-12 md:space-y-16">
        <HeroCarousel items={heroItems} />

        <section>
          <SectionHeading
            title="主播分类快捷入口"
            subtitle="按类型快速找到你感兴趣的女主播"
          />
          <CategoryShortcuts />
        </section>

        <section>
          <SectionHeading
            title="最新动态"
            subtitle="按时间倒序更新的韩国女主播新闻资讯"
            action={
              <Link
                to="/category"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <LatestNews news={latestNews} />
            </div>
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-20 rounded-lg bg-background-100 border border-background-200/70 p-4">
                <h3 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 mb-3">
                  <i className="ri-trophy-line text-accent-500"></i>
                  热门主播榜
                </h3>
                <HotStreamers streamers={hotStreamers} />
                <Link
                  to="/category?sort=hot"
                  className="mt-3 flex items-center justify-center gap-1 w-full py-2.5 rounded-md bg-secondary-100 text-secondary-800 text-sm font-medium hover:bg-secondary-200 transition-colors"
                >
                  查看完整榜单
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>
        </section>

        <section>
          <SectionHeading
            title="热门视频精选"
            subtitle="采访、节目片段与写真 MV 一网打尽"
            accent
            action={
              <Link
                to="/videos"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 whitespace-nowrap"
              >
                更多视频
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <HotVideos videos={hotVideos} onPlay={setPlaying} />
        </section>

        <section>
          <SectionHeading
            title="推荐写真专题"
            subtitle="精选女主播高清写真，红毯与日常一次看个够"
            action={
              <Link
                to="/gallery"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-accent-600 hover:text-accent-700 whitespace-nowrap"
              >
                进入图库
                <i className="ri-arrow-right-line"></i>
              </Link>
            }
          />
          <FeaturedGallery items={featuredGallery} />
        </section>

        <section className="rounded-lg bg-primary-700 text-background-50 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-3">关于韩国女主播</h2>
            <p className="text-sm text-background-200/90 leading-relaxed max-w-2xl">
              我们专注韩国女主播资讯领域，聚合新闻动态、个人资料、视频专辑与高清写真，为关注韩国娱乐文化的粉丝提供一站式的内容浏览体验。内容持续更新，分类清晰，帮助你随时掌握喜爱主播的最新动向。
            </p>
          </div>
          <div className="lg:col-span-1 flex lg:justify-end">
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </div>

      <VideoModal video={playing} onClose={() => setPlaying(null)} />
    </>
  );
}